import { IProjectType, IQuery } from '@app/@introspection';
import { AddProjectModel } from '@app/projects/list/components/add-project-form.component';
import { Injectable } from '@rxdi/core';
import { deepCopy, importQuery } from '@rxdi/graphql-client';
import { map } from 'rxjs/operators';

import { BaseGraphqlService } from '../base';
import { _writeProjectState } from './project-state.mutators';

@Injectable()
export class ProjectsService extends BaseGraphqlService {
  getProject({ id }: IProjectType) {
    return this.query({
      query: 'get-project.query.graphql',
      variables: {
        id,
      },
    }).pipe(map((res) => res.data.getProject));
  }

  updateAgentPurpose({ id }: IProjectType, agentPurpose: string) {
    return this.mutate({
      mutation: 'update-agent-purpose.graphql',
      variables: {
        projectId: id,
        agentPurpose,
      },
    }).pipe(map((res) => res.data.updateAgentPurpose));
  }

  getProjectStatistics({ id }: IProjectType) {
    return this.query({
      query: 'get-project.query.graphql',
      variables: {
        id,
      },
    }).pipe(map((res) => res.data.getProjectStatistics));
  }

  getProjectBoards({ id }: IProjectType) {
    return this.query({
      query: 'get-project-boards.query.graphql',
      variables: {
        id,
      },
    }).pipe(map((res) => res.data.getProjectBoards));
  }

  getProjectBoardsSimple({ id }: IProjectType) {
    return this.query({
      query: 'get-project-boards-simple.query.graphql',
      variables: {
        id,
      },
    }).pipe(map((res) => res.data.getProjectBoards));
  }

  watchProject({ id }: IProjectType) {
    return this.watchQuery({
      query: 'get-project.query.graphql',
      variables: {
        id,
      },
    }).pipe(map((res) => res.data.getProject));
  }

  listProjects() {
    return this.watchQuery({
      query: 'list-projects.query.graphql',
    }).pipe(map(({ data }) => data.listProjects));
  }

  createProject(variables: AddProjectModel) {
    return this.mutate({
      mutation: 'create-project.mutation.graphql',
      variables,
      update(proxy, { data: { createProject } }) {
        const query = importQuery('list-projects.query.graphql');
        const cache = deepCopy(
          proxy.readQuery<IQuery>({
            variables,
            query,
          })
        );
        cache.listProjects = cache.listProjects || [];
        proxy.writeQuery<IQuery>({
          query,
          data: {
            listProjects: cache.listProjects.concat([createProject]),
          },
        });
      },
    }).pipe(map((res) => res.data.createProject));
  }

  assignMachineToProject(variables: { projectId: string; machineId: string }) {
    return this.mutate({
      mutation: 'assign-project-machine.mutation.graphql',
      variables,
      update: (proxy, { data: { assignMachineToProject } }) => _writeProjectState(assignMachineToProject)(proxy),
    }).pipe(map((res) => res.data.assignMachineToProject));
  }

  removeMachineFromProject(variables: { projectId: string; machineId: string }) {
    return this.mutate({
      mutation: 'remove-project-machine.mutation.graphql',
      variables,
      update: (proxy, { data: { removeMachineFromProject } }) => _writeProjectState(removeMachineFromProject)(proxy),
    }).pipe(map((res) => res.data.removeMachineFromProject));
  }

  deleteProject({ id }: IProjectType) {
    return this.mutate({
      mutation: 'delete-project.mutation.graphql',
      variables: { id },
      update(proxy) {
        const query = importQuery('list-projects.query.graphql');
        const cache = deepCopy(
          proxy.readQuery<IQuery>({
            query,
          })
        );
        const listProjects = cache.listProjects.filter((p) => p.id !== id);
        proxy.writeQuery<IQuery>({
          query,
          data: { listProjects },
        });
      },
    }).pipe(map((res) => res.data.deleteProject));
  }

  subscribeToProjectCreation() {
    return this.subscribe({
      query: 'create-project.subscription.graphql',
    }).pipe(map((res) => res.data.subscribeToCreateProject));
  }

  listTokens(projectId: string) {
    return this.query({
      query: 'list-tokens.query.graphql',
      variables: { projectId },
    }).pipe(map(({ data }) => data.listTokens));
  }

  createToken(name: string, projectId: string) {
    return this.mutate({
      mutation: 'create-token.mutation.graphql',
      variables: { name, projectId },
    }).pipe(map(({ data }) => data.createToken));
  }

  blackListToken(id: string, projectId: string, blacklisted: boolean) {
    return this.mutate({
      mutation: 'blacklist-token.mutation.graphql',
      variables: { id, blacklisted, projectId },
    }).pipe(map(({ data }) => data.blackListToken));
  }

  deleteToken(id: string, projectId: string) {
    return this.mutate({
      mutation: 'delete-token.mutation.graphql',
      variables: { id, projectId },
    }).pipe(map(({ data }) => data.deleteToken));
  }
}
